import { useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { useBackGuard, type BackGuard } from '@/hooks/use-back-guard';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { UserRound } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BusinessCategoryPicker } from '@/components/common/business-category-picker';
import { giveAskApi, giveAskHeading, type GiveAskItem, type ReplyPayload } from '@/lib/give-ask-api';
import {
  beneficiaryPayload,
  emptyBeneficiary,
  isBeneficiaryValid,
  type BeneficiaryValue,
} from '@/lib/beneficiary';
import { BeneficiaryFields } from '@/components/conclave/beneficiary-fields';


interface SendLeadSheetProps {
  communityId: string | null;
  eventId?: string | null;
  target: GiveAskItem | null;
  owner?: { name: string; image: string | null } | null;
  onOpenChange: (open: boolean) => void;
  onSent: (giveAskId: string) => void;
}

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function SendLeadSheet({ communityId, eventId, target, owner, onOpenChange, onSent }: SendLeadSheetProps) {
  const [source, setSource] = useState<'mine' | 'manual'>('mine');
  const [myGives, setMyGives] = useState<GiveAskItem[]>([]);
  const [offeredId, setOfferedId] = useState<string | null>(null);
  const [manualName, setManualName] = useState('');
  const [manualBusiness, setManualBusiness] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualCategory, setManualCategory] = useState<{ businessCategory: string; subCategory: string } | null>(null);
  const [message, setMessage] = useState('');
  const [beneficiary, setBeneficiary] = useState<BeneficiaryValue>(emptyBeneficiary());
  const messageRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);

  const open = target !== null;
  const [discardOpen, setDiscardOpen] = useState(false);
  const pendingBack = useRef<BackGuard | null>(null);
  const dirty = Boolean(
    offeredId ||
      manualName.trim() ||
      manualBusiness.trim() ||
      manualDescription.trim() ||
      message.trim() ||
      beneficiary.name.trim()
  );
  const requestClose = () => {
    if (!open) return;
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };
  useBackGuard(open, (back) => {
    if (!dirty) {
      onOpenChange(false);
      return;
    }
    pendingBack.current = back;
    setDiscardOpen(true);
  });

  useEffect(() => {
    if (!open) return;
    giveAskApi
      .listOwn('GIVE')
      .then((data) => setMyGives(data.items))
      .catch(() => setMyGives([]));
  }, [open]);

  useEffect(() => {
    setSource('mine');
    setOfferedId(null);
    setManualName('');
    setManualBusiness('');
    setManualDescription('');
    setManualCategory(null);
    setMessage('');
    setBeneficiary(emptyBeneficiary());
  }, [target?.id]);

  const submit = async () => {
    if (!target) return;

    if (!isBeneficiaryValid(beneficiary)) {
      toast.error('Enter the name of the person this lead is for');
      return;
    }
    const context = eventId ? { eventId } : { communityId };
    const who = beneficiaryPayload(beneficiary);
    let payload: ReplyPayload;
    if (source === 'mine') {
      if (!offeredId) {
        toast.error('Choose one of your gives');
        return;
      }
      payload = { ...context, ...who, message: message.trim(), offeredGiveAskId: offeredId };
    } else {
      if ((!manualName.trim() && !manualBusiness.trim()) || manualDescription.trim().length < 3) {
        toast.error('Enter the name or business of the give, and a short note');
        return;
      }
      payload = {
        ...context,
        ...who,
        message: message.trim(),
        manual: {
          contactName: manualName.trim() || null,
          businessName: manualBusiness.trim() || null,
          description: manualDescription.trim(),
          category: manualCategory?.businessCategory ?? null,
          subCategory: manualCategory?.subCategory ?? null,
        },
      };
    }

    setSubmitting(true);
    try {
      await giveAskApi.reply(target.id, payload);
      toast.success('Lead sent to the ask owner');
      onSent(target.id);
      onOpenChange(false);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send your reply'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <ConfirmDialog
      open={discardOpen}
      title="Discard this lead?"
      description="The give you picked, your details and your message will be lost."
      confirmLabel="Discard"
      destructive
      onCancel={() => {
        pendingBack.current?.stay();
        pendingBack.current = null;
        setDiscardOpen(false);
      }}
      onConfirm={() => {
        pendingBack.current = null;
        setDiscardOpen(false);
        onOpenChange(false);
      }}
    />
    <Sheet open={open} onOpenChange={(next) => { if (!next) requestClose(); }}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>Reply to this ask with a lead</SheetTitle>
        </SheetHeader>
        {target ? (
          <div className="mt-3 rounded-xl border bg-muted/30 p-4">
            {owner ? (
              <div className="mb-3 flex items-center gap-2">
                <Avatar className="h-8 w-8 shrink-0 rounded-full border-2 border-primary/40 ring-2 ring-primary/15">
                  <AvatarImage src={owner.image ?? undefined} alt={owner.name} />
                  <AvatarFallback>
                    <UserRound className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <p className="text-sm font-medium">{owner.name} asked</p>
              </div>
            ) : null}
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Ask</p>
            <p className="mt-1 font-medium">{giveAskHeading(target)}</p>
            <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{target.description}</p>
          </div>
        ) : null}

        <div className="mt-4 space-y-5">
          <BeneficiaryFields value={beneficiary} onChange={setBeneficiary} />

          <div className="flex gap-2">
            <Button size="sm" variant={source === 'mine' ? 'default' : 'outline'} onClick={() => setSource('mine')}>
              Offer one of my gives
            </Button>
            <Button size="sm" variant={source === 'manual' ? 'default' : 'outline'} onClick={() => setSource('manual')}>
              Write a new give
            </Button>
          </div>


          {source === 'mine' ? (
            <div className="space-y-2">
              {myGives.length === 0 ? (
                <p className="text-sm text-muted-foreground">You have no gives yet. Write one instead.</p>
              ) : (
                myGives.map((give) => (
                  <label
                    key={give.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${
                      offeredId === give.id ? 'border-teal-600 bg-teal-50' : 'bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name="offered-give"
                      className="mt-1"
                      checked={offeredId === give.id}
                      onChange={() => {
                        setOfferedId(give.id);
                        setBeneficiary({
                          beneficiary: 'other',
                          name: give.contactName ?? '',
                          phone: give.contactPhone ?? '',
                          email: give.contactEmail ?? '',
                          relation: '',
                        });
                        requestAnimationFrame(() =>
                          messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        );
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block font-medium">{giveAskHeading(give)}</span>
                      <span className="block text-sm text-muted-foreground line-clamp-2">{give.description}</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <Input placeholder="Name of the person" value={manualName} onChange={(e) => setManualName(e.target.value)} />
              <Input placeholder="Business name" value={manualBusiness} onChange={(e) => setManualBusiness(e.target.value)} />
              <Textarea
                placeholder="Notes about them or their business"
                value={manualDescription}
                onChange={(e) => setManualDescription(e.target.value)}
                maxLength={1000}
              />
              <BusinessCategoryPicker
                businessCategory={manualCategory?.businessCategory}
                subCategory={manualCategory?.subCategory}
                onSelect={setManualCategory}
              />
            </div>
          )}

          <div ref={messageRef} className="space-y-2">
            <label className="text-sm font-medium" htmlFor="lead-message">
              Message to the ask owner (optional)
            </label>
            <Textarea
              id="lead-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell them how you can help"
              maxLength={1000}
            />
          </div>


          <Button className="w-full" onClick={submit} disabled={submitting}>
            {submitting ? 'Sending…' : 'Send lead'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
}
